import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { BarChart3, CheckCircle, TrendingUp, AlertTriangle, Target, Clock, MapPin, Loader2 } from "lucide-react";

export default function CoordinatorKPIs() {
  const { user } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [myStats, setMyStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Try admin KPIs (works for admin, fails for coordinators)
        try {
          const res = await api.get("/recruitment/kpis");
          setKpis(res.data);
        } catch {}

        // Calculate personal stats from prospects list
        const res = await api.get("/recruitment/prospects");
        const all = res.data.prospects || [];
        const mine = all.filter(p => p.assigned_coordinator_id === user?.user_id);
        const live = mine.filter(p => p.current_stage === "live_published").length;
        const verbal = mine.filter(p => p.current_stage === "verbal_commitment").length;
        const onboarding = mine.filter(p => p.current_stage === "onboarding_in_progress").length;
        const stalled = mine.filter(p => !["live_published","stalled_lost"].includes(p.current_stage) && p.days_in_stage > 7).length;
        setMyStats({
          total: mine.length, live, verbal, onboarding, stalled,
          conversion: mine.length > 0 ? Math.round(live / mine.length * 100) : 0,
          all_total: all.length,
          all_live: all.filter(p => p.current_stage === "live_published").length,
        });
      } catch { toast.error("Failed to load KPIs"); }
      setLoading(false);
    };
    fetchData();
  }, [user]);

  if (loading) return <div className="flex justify-center items-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6" data-testid="coordinator-kpis">
      <h1 className="font-heading text-2xl sm:text-3xl flex items-center gap-2"><BarChart3 className="w-6 h-6 text-primary" />My Performance</h1>

      {/* Personal KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card><CardContent className="p-4 text-center">
          <Target className="w-5 h-5 text-blue-500 mx-auto mb-1" />
          <p className="text-2xl font-heading font-bold">{myStats?.total || 0}</p>
          <p className="text-xs text-muted-foreground">My Prospects</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <CheckCircle className="w-5 h-5 text-green-500 mx-auto mb-1" />
          <p className="text-2xl font-heading font-bold">{myStats?.live || 0}</p>
          <p className="text-xs text-muted-foreground">Venues Live</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <TrendingUp className="w-5 h-5 text-purple-500 mx-auto mb-1" />
          <p className="text-2xl font-heading font-bold">{myStats?.conversion || 0}%</p>
          <p className="text-xs text-muted-foreground">Conversion</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <AlertTriangle className="w-5 h-5 text-amber-500 mx-auto mb-1" />
          <p className="text-2xl font-heading font-bold">{myStats?.stalled || 0}</p>
          <p className="text-xs text-muted-foreground">Stalled Deals</p>
        </CardContent></Card>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Card><CardContent className="p-3 text-center">
          <p className="text-lg font-bold text-amber-600">{myStats?.verbal || 0}</p>
          <p className="text-[10px] text-muted-foreground">Verbal Commitments</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-lg font-bold text-emerald-600">{myStats?.onboarding || 0}</p>
          <p className="text-[10px] text-muted-foreground">In Onboarding</p>
        </CardContent></Card>
      </div>

      {/* Team comparison (if KPIs available) */}
      {kpis?.coordinators && (
        <Card>
          <CardHeader><CardTitle className="font-heading text-base">Team Leaderboard</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {kpis.coordinators.map((c, i) => (
                <div key={i} className={`flex items-center justify-between py-2 px-2 rounded ${c.id === user?.user_id ? "bg-primary/5 border border-primary/20" : "border-b last:border-0"}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-muted-foreground w-5">#{i+1}</span>
                    <span className="text-sm font-medium">{c.name}</span>
                    {c.id === user?.user_id && <Badge className="text-[8px]">You</Badge>}
                  </div>
                  <div className="flex items-center gap-4 text-xs">
                    <span>{c.total} prospects</span>
                    <span className="font-bold text-green-600">{c.live} live</span>
                    <span className="text-primary">{c.conversion}%</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Platform totals */}
      {myStats && (
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div><p className="text-sm font-medium">Platform Total</p><p className="text-xs text-muted-foreground">All coordinators combined</p></div>
            <div className="flex gap-6 text-center">
              <div><p className="font-bold">{myStats.all_total}</p><p className="text-[10px] text-muted-foreground">Prospects</p></div>
              <div><p className="font-bold text-green-600">{myStats.all_live}</p><p className="text-[10px] text-muted-foreground">Live</p></div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
